import { lazy, Suspense } from 'react';

import { Switch } from 'react-router-dom';

import { isProd } from '@m/constants/FeatureFlags';
import { useAdminFeatureFlag } from '@m/hooks/data/featureFlags';
import { RoleName } from '@m/types/api/iam/Iam';
import { ListPageWrapper } from 'apps/admin/AdminApp.styled';
import AuthenticatedRoute from 'apps/admin/components/AuthenticatedRoute';
import useSuperAdminCheck from 'apps/admin/hooks/useSuperAdminCheck';
import { hasRoles } from 'apps/admin/permissions/RouteManager';

const NotificationContainer = lazy(() => import('apps/admin/containers/NotificationContainer'));
const PaymentCenterContainer = lazy(() => import('apps/admin/containers/PaymentCenterContainer/PaymentCenterContainer'));
const PaymentDetailView = lazy(
  () => import('apps/admin/containers/PaymentCenterContainer/PaymentDetailView'),
);
const BulkEntSubCreateContainer = lazy(
  () => import('apps/admin/containers/BulkEntSubCreateContainer'),
);
const VisitChargeManagementPage = lazy(
  () => import('apps/admin/containers/VisitChargeManagementPage'),
);
const ManagerCleanupPage = lazy(() => import('apps/admin/containers/ManagerCleanupPage'));
const HackathonToolingPage = lazy(
  () => import('apps/admin/containers/HackathonToolingPage/HackathonToolingPage'),
);

export const devToolRoutesConfig = [
  {
    exact: true,
    path: '/notification-center',
    breadcrumb: 'Notifications',
    roles: [RoleName.MANAGER, RoleName.CUSTOMER_SUPPORT],
    component: AuthenticatedRoute,
    children: (
      <Suspense fallback={null}>
        <ListPageWrapper>
          <NotificationContainer />
        </ListPageWrapper>
      </Suspense>
    ),
  },
  {
    exact: true,
    path: '/payment-center',
    breadcrumb: 'Payment Center',
    roles: [RoleName.MANAGER, RoleName.CUSTOMER_SUPPORT],
    component: AuthenticatedRoute,
    children: (
      <Suspense fallback={null}>
        <ListPageWrapper>
          <PaymentCenterContainer />
        </ListPageWrapper>
      </Suspense>
    ),
  },
  {
    exact: true,
    path: '/payment-center/:customerId',
    breadcrumb: ({ match: { params } }: { match: { params: { customerId: string } } }) => (
      <span>{params.customerId}</span>
    ),
    roles: [RoleName.MANAGER, RoleName.CUSTOMER_SUPPORT],
    component: AuthenticatedRoute,
    children: (
      <Suspense fallback={null}>
        <ListPageWrapper>
          <PaymentDetailView />
        </ListPageWrapper>
      </Suspense>
    ),
  },
  {
    exact: true,
    path: '/visit-charge-management',
    breadcrumb: 'Visit Charge Management',
    roles: [RoleName.SUPER_ADMIN],
    component: AuthenticatedRoute,
    children: (
      <Suspense fallback={null}>
        <ListPageWrapper>
          <VisitChargeManagementPage />
        </ListPageWrapper>
      </Suspense>
    ),
  },
  {
    exact: true,
    path: '/bulk-creator',
    breadcrumb: 'Create Bulk Enterprises/Subscriptions',
    roles: [RoleName.SUPER_ADMIN],
    component: AuthenticatedRoute,
    children: (
      <Suspense fallback={null}>
        <ListPageWrapper>
          <BulkEntSubCreateContainer />
        </ListPageWrapper>
      </Suspense>
    ),
  },
  {
    exact: true,
    path: '/manager-cleanup',
    breadcrumb: 'Manager app cleanup',
    roles: [RoleName.SUPER_ADMIN],
    component: !isProd ? AuthenticatedRoute : null,
    children: (
      <Suspense fallback={null}>
        <ListPageWrapper>
          <ManagerCleanupPage />
        </ListPageWrapper>
      </Suspense>
    ),
  },
  {
    exact: true,
    path: '/hackathon-tooling',
    breadcrumb: 'Hackathon Tooling',
    roles: [RoleName.SUPER_ADMIN],
    component: AuthenticatedRoute,
    children: (
      <Suspense fallback={null}>
        <ListPageWrapper>
          <HackathonToolingPage />
        </ListPageWrapper>
      </Suspense>
    ),
  },
];

export default function DevToolRoutes() {
  const isSuperAdmin = useSuperAdminCheck();
  const { data: paymentCenterEnabled } = useAdminFeatureFlag('PaymentCenterView');

  return (
    <Switch>
      {devToolRoutesConfig.map(({ component: Component, breadcrumb, roles, ...props }) => {
        // Skip payment center routes if feature flag is disabled
        if (props.path?.includes('/payment-center') && !paymentCenterEnabled) {
          return null;
        }

        // If the route specifies roles, check those roles; otherwise require super admin
        const hasAccess = roles ? hasRoles(roles) : isSuperAdmin;

        return hasAccess && Component ? <Component key={props.path} {...props} /> : null;
      })}
    </Switch>
  );
}
